import React, { useCallback, useEffect, useState } from "react";
import Detail from "./Detail";
import style from "css/room.module.css";
import { VideoCameraOutlined } from "@ant-design/icons";
import Resource from "./Resource";
import { Button, ConfigProvider } from "antd";
import { useSelector } from "react-redux";
import ResPop from "components/mobile/mypage/ResPop";
import dayjs from "dayjs";
import DetailResPop from "components/mobile/mypage/DetailResPop";
import { useNavigate, useSearchParams } from "react-router-dom";

const currentType = "current";
const pastType = "past";

/**
 * 파일명: list.js
 * 작성자: 권도훈
 * 작성일자: 25.02.24
 * 설명: 모바일 페이지 회의실 현황 목록
 */
function List({ roomList, scheduleList, date, onRefresh, selectRoom }) {
  // redux
  const payload = useSelector((state) => state.user);
  const systemPayload = useSelector((state) => state.system);

  //searchParam
  const [searchParams, setSearchParams] = useSearchParams();

  //navigate
  const navigate = useNavigate();

  //status
  const [room, setRoom] = useState({});
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [time, setTime] = useState({
    start: "",
    end: "",
  });
  // reserve popup state
  const [openResPop, setOpenResPop] = useState(false);
  const [openDetail, setOpenDetail] = useState(false);
  const [reserveType, setReserveType] = useState("");
  const [detailType, setDetailType] = useState("");
  const [reserveInfo, setReserveInfo] = useState({
    EMAIL: payload.id,
    HOST_NM: payload.name,
    INPUT_ID: payload.id,
    CONTENTS: "",
    DURATION: 0,
    ENDPOINT_LIST: [],
    END_DATE: "",
    END_TIME: dayjs().add(30, "minute").format("YYYY-MM-DD HH:mm"),
    FILE_INFO: "",
    MAIL_YN: "N",
    MEETING_PW: "",
    OPEN_YN: "Y",
    PARTILIST: "",
    RECORD_YN: "N",
    SCHEDULE_ID: "",
    SCHEDULE_TYPE: "",
    START_DATE: "",
    START_TIME: dayjs().format("YYYY-MM-DD HH:mm"),
    TITLE: "",
    USE_YN: "Y",
    PROCESS_TYPE: "",
  });

  const resetReserveInfo = useCallback(() => {
    setReserveInfo({
      ...reserveInfo,
      EMAIL: "",
      HOST_NM: "",
      INPUT_ID: "",
      CONTENTS: "",
      DURATION: 0,
      END_DATE: "",
      END_TIME: "",
      FILE_INFO: "",
      MAIL_YN: "N",
      MEETING_PW: "",
      OPEN_YN: "Y",
      RECORD_YN: "N",
      SCHEDULE_ID: "",
      SCHEDULE_TYPE: "",
      START_DATE: "",
      START_TIME: "",
      TITLE: "",
      ENDPOINT_LIST: [],
      PARTICIPANT_LIST: [],
      ATTACHMENT_LIST: [],
      USE_YN: "Y",
      PROCESS_TYPE: "",
    });
  }, [reserveInfo]);

  useEffect(() => {
    if (reserveInfo.PROCESS_TYPE !== "" && !openResPop && !openDetail) {
      resetReserveInfo();
    }
  }, [openDetail, openResPop, reserveInfo.PROCESS_TYPE, resetReserveInfo]);

  const timeFormat = (time) => {
    return (
      date.format("YYYY-MM-DD") + "T" + dayjs(new Date(time)).format("HH:mm")
    );
  };

  const onOpenReserve = (reserveType, room) => {
    setReserveInfo({
      ...reserveInfo,
      EMAIL: payload.id,
      HOST_NM: payload.name,
      INPUT_ID: payload.id,
      START_TIME:
        time.start !== ""
          ? timeFormat(time.start)
          : dayjs().format("YYYY-MM-DD HH:mm"),
      END_TIME:
        time.end !== ""
          ? timeFormat(time.end)
          : dayjs().add(30, "minute").format("YYYY-MM-DD HH:mm"),
      ENDPOINT_LIST: [
        {
          ENDPOINT_ID: room.id,
          ENDPOINT_NM: room.title,
          ENDPOINT_TYPE: room.ENDPOINT_TYPE,
        },
      ],
      SCHEDULE_TYPE: systemPayload.systemType,
      PROCESS_TYPE: "C",
    });
    setReserveType(reserveType);
    setOpenResPop(true);
  };

  const onOpenEdit = (data, room) => {
    if (
      new Date() > new Date(data.START_TIME) ||
      new Date() > new Date(data.END_TIME)
    ) {
      setDetailType(pastType);
    } else {
      setDetailType(currentType);
    }

    const schedule = {
      ...reserveInfo,
      EMAIL: data.HOST_EMAIL,
      HOST_NM: data.HOST_NM,
      INPUT_ID: data.INPUT_ID,
      CONTENTS: data.CONTENTS,
      DURATION: data.DURATION,
      ENDPOINT_LIST: [{ ENDPOINT_ID: room.id, ENDPOINT_NM: room.title }],
      END_DATE: data.END_DATE,
      END_TIME: data.END_TIME,
      FILE_INFO: data.FILE_INFO,
      MAIL_YN: data.MAIL_YN,
      MEETING_PW: data.MEETING_PW,
      OPEN_YN: data.OPEN_YN,
      PARTILIST: data.PARTILIST,
      RECORD_YN: data.RECORD_YN,
      SCHEDULE_ID: data.SCHEDULE_ID,
      SCHEDULE_TYPE: data.SCHEDULE_TYPE,
      START_DATE: data.START_DATE,
      START_TIME: data.START_TIME,
      TITLE: data.TITLE,
      USE_YN: data.USE_YN,
      PROCESS_TYPE: "U",
    };
    setReserveInfo(schedule);
    setOpenDetail(true);
  };

  const onCloseResPop = () => {
    setReserveInfo({
      EMAIL: "",
      HOST_NM: "",
      INPUT_ID: "",
      CONTENTS: "",
      DURATION: 0,
      ENDPOINT_LIST: [],
      END_DATE: "",
      END_TIME: "",
      FILE_INFO: "",
      MAIL_YN: "N",
      MEETING_PW: "",
      OPEN_YN: "Y",
      PARTILIST: "",
      RECORD_YN: "N",
      SCHEDULE_ID: "",
      SCHEDULE_TYPE: "",
      START_DATE: "",
      START_TIME: "",
      TITLE: "",
      USE_YN: "Y",
      PROCESS_TYPE: "",
    });
    setReserveType("");
    setTime({
      start: "",
      end: "",
    });
    setOpenResPop(false);
  };

  const onCloseDetail = () => {
    setOpenDetail(false);
  };

  const onEditSchedule = (res, process_type) => {
    setReserveInfo({
      ...reserveInfo,
      ENDPOINT_LIST: res.endpointlist,
      PARTICIPANT_LIST: res.participantlist,
      ATTACHMENT_LIST: res.attachmentlist,
      PROCESS_TYPE: process_type,
    });
    setReserveType("general");
    setOpenResPop(true);
  };

  // useEffect(() => {
  //   const endpointId = searchParams.get("endpointId");
  //   if (endpointId !== null && roomList.length > 0) {
  //     if (payload.id !== "") {
  //       //로그인 되어있음
  //       let tmpRoom = roomList.filter((room) => endpointId === room.id);
  //       setRoom(tmpRoom[0]);
  //       onOpenReserve("general", tmpRoom[0]);
  //     } else {
  //       //비로그인 로그인 페이지로 이동
  //       console.log("로그인 페이지 이동");
  //       navigate(
  //         process.env.REACT_APP_CLIENT_URL + "?endpointId=" + endpointId
  //       );
  //     }
  //   }
  // }, [searchParams, roomList]);

  useEffect(() => {
    if (selectRoom?.id !== undefined && selectRoom?.id !== null) {
      setRoom(selectRoom);
      onOpenReserve("general", selectRoom);
    }
  }, [selectRoom]);

  return (
    <>
      <Detail
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        room={room}
      />

      <ResPop
        isOpenRes={openResPop && reserveType !== ""}
        close={onCloseResPop}
        schedule={reserveInfo}
        getScheduleList={onRefresh}
        PAGE_TYPE="room"
        RES_TYPE={reserveType}
      />

      <DetailResPop
        isOpenResDetail={openDetail}
        openResPop={onEditSchedule}
        closeResDetail={onCloseDetail}
        schedule={reserveInfo}
        type={detailType}
        refresh={onRefresh}
      />

      <section className={style.list}>
        <p className={style.list_title}>회의실 ({roomList.length})</p>

        <section className={style.list_content}>
          {roomList.length > 0 &&
            roomList.map((room, idx) => (
              <Item
                key={idx}
                room={room}
                date={date}
                data={scheduleList.filter(
                  (schedule) => schedule.data.ENDPOINT_ID === room.id
                )}
                time={time}
                setTime={(res) => setTime(res)}
                onDetail={(res) => {
                  setRoom(res);
                  setIsDetailOpen(true);
                }}
                onReserve={() => {
                  onOpenReserve("general", room);
                }}
                onEdit={(res) => onOpenEdit(res, room)}
              />
            ))}
        </section>
      </section>
    </>
  );
}

export default List;

function Item({
  room,
  date,
  data,
  time,
  setTime,
  onDetail,
  onReserve,
  onEdit,
}) {
  return (
    <div className={style.list_item}>
      <section className={style.list_item_header}>
        <p className={style.list_item_title}>{room.title}</p>
        <VideoCameraOutlined />
      </section>

      <section className={style.list_item_body}>
        <Resource
          date={date}
          data={data}
          time={time}
          setTime={setTime}
          onEdit={onEdit}
        />
      </section>

      <section className={style.list_item_footer}>
        <ConfigProvider
          theme={{
            components: {
              Button: {
                defaultColor: "#121212",
                defaultBorderColor: "#121212",
                defaultHoverBorderColor: "#121212",
                defaultHoverColor: "#121212",
                defaultActiveBorderColor: "#121212",
                defaultactiveColor: "#121212",
                defaultGhostBorderColor: "#121212",
                defaultGhostColor: "#121212",
                colorPrimaryHover: "#121212",
                colorPrimaryActive: "#121212",
              },
            },
          }}
        >
          <Button
            className={style.list_item_footer_btn}
            type="default"
            onClick={() => onDetail(room)}
          >
            회의실 정보
          </Button>
        </ConfigProvider>

        <ConfigProvider
          theme={{
            components: {
              Button: {
                defaultColor: "#1cb691",
                defaultBorderColor: "#1cb691",
                defaultHoverBorderColor: "#1cb691",
                defaultHoverColor: "#1cb691",
                defaultActiveBorderColor: "#1cb691",
                defaultactiveColor: "#1cb691",
                defaultGhostBorderColor: "#1cb691",
                defaultGhostColor: "#1cb691",
                colorPrimaryHover: "#1cb691",
                colorPrimaryActive: "#1cb691",
              },
            },
          }}
        >
          <Button
            className={style.list_item_footer_btn}
            type="default"
            onClick={(e) => {
              e.stopPropagation();
              onReserve();
            }}
          >
            예약하기
          </Button>
        </ConfigProvider>
      </section>
    </div>
  );
}
